import { useState, useEffect, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import Button from '../ui/Button.jsx';
import TerminalWindow from '../primitives/TerminalWindow.jsx';
import { cta } from '../../content/site.js';

/**
 * TerminalSimulator — interactive terminal CLI simulation of Merlin.
 * Renders selectable operations sectors that trigger typing diagnostic routines
 * and reveal custom automation recommendation cards.
 */
export default function TerminalSimulator() {
  const [step, setStep] = useState('idle'); // 'idle' | 'typing' | 'diagnosing' | 'complete'
  const [selectedSector, setSelectedSector] = useState(null);
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState(0);
  const scrollRef = useRef(null);

  // Auto-scroll to bottom of terminal content on updates
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs, step, progress]);

  // Handle typing diagnostic logs line-by-line
  useEffect(() => {
    if (step !== 'typing' || !selectedSector) return;

    let lineIndex = 0;
    const interval = setInterval(() => {
      if (lineIndex < selectedSector.logs.length) {
        setLogs((prev) => [...prev, selectedSector.logs[lineIndex]]);
        lineIndex++;
      } else {
        clearInterval(interval);
        setStep('diagnosing');
      }
    }, 350);

    return () => clearInterval(interval);
  }, [step, selectedSector]);

  // Handle diagnostic progress ticking
  useEffect(() => {
    if (step !== 'diagnosing') return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev < 100) {
          return prev + 10;
        } else {
          clearInterval(interval);
          setStep('complete');
          return 100;
        }
      });
    }, 80);

    return () => clearInterval(interval);
  }, [step]);

  const startSimulation = (sector) => {
    setSelectedSector(sector);
    setLogs([]);
    setProgress(0);
    setStep('typing');
  };

  const resetTerminal = () => {
    setSelectedSector(null);
    setLogs([]);
    setProgress(0);
    setStep('idle');
  };

  return (
    <TerminalWindow
      label="merlin@artificers:~$"
      className="w-full min-h-[380px] flex flex-col justify-between"
    >
      <div ref={scrollRef} className="max-h-[420px] overflow-y-auto pr-1">
        {/* CLI Session Start */}
        <div className="text-brand-blue-bright font-semibold mb-1">
          {'>'} {cta.simulator.welcomeCommand}
        </div>
        <div className="text-ink-300 mb-2 font-mono text-xs">
          {cta.simulator.systemResponse}
        </div>
        <div className="text-brand-purple mb-4 font-mono text-sm leading-relaxed">
          {cta.simulator.merlinGreeting}
        </div>

        {/* STEP: IDLE - Sector Selection Buttons */}
        {step === 'idle' && (
          <div className="flex flex-col gap-2 mt-4">
            {cta.simulator.sectors.map((s, idx) => (
              <button
                key={s.id}
                onClick={() => startSimulation(s)}
                className="text-left w-full p-3.5 rounded-[10px] border border-white/10 bg-white/[.02] hover:bg-brand-blue/10 hover:border-brand-blue-bright/40 transition flex items-center justify-between group"
              >
                <span className="font-mono text-xs text-ink-100">
                  <span className="text-brand-blue-bright font-bold mr-2">
                    [0{idx + 1}]
                  </span>
                  {s.label}
                </span>
                <span className="text-ink-400 group-hover:text-brand-blue-bright transition-colors text-mono-xs font-mono font-medium">
                  Run Diagnostic
                </span>
              </button>
            ))}
          </div>
        )}

        {/* STEP: RUNNING & TYPING DIAGNOSTICS */}
        {step !== 'idle' && selectedSector && (
          <div className="font-mono text-xs">
            <div className="text-ink-50 mt-2 font-semibold">
              {'>'} analyze --target={selectedSector.code}
            </div>
            
            {/* Typewritten Log Lines */}
            {logs.map((log, index) => (
              <div key={index} className="text-ink-300 mt-1.5">
                {log}
              </div>
            ))}

            {/* Simulated Progress Bar */}
            {step === 'diagnosing' && (
              <div className="flex items-center gap-3 mt-4">
                <span className="text-brand-blue-bright font-bold">DIAGNOSTIC:</span>
                <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-blue-bright transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <span className="text-ink-100 w-8 text-right">{progress}%</span>
              </div>
            )}

            {/* STEP: COMPLETE - Recommendation Card */}
            {step === 'complete' && (
              <div className="mt-5 p-5 rounded-[10px] border-l-2 border-brand-blue-bright bg-white/[.01] glass-card text-ink-200">
                <h4 className="font-display font-bold text-sm text-ink-50 mb-3">
                  {selectedSector.result.title}
                </h4>
                <div className="space-y-2 text-xs font-mono">
                  <div className="flex">
                    <span className="text-brand-blue-bright font-bold w-32 shrink-0">Source:</span>
                    <span className="text-ink-100">{selectedSector.result.source}</span>
                  </div>
                  <div className="flex">
                    <span className="text-brand-blue-bright font-bold w-32 shrink-0">Core Engine:</span>
                    <span className="text-ink-100">{selectedSector.result.engine}</span>
                  </div>
                  <div className="flex">
                    <span className="text-brand-blue-bright font-bold w-32 shrink-0">Action:</span>
                    <span className="text-ink-100">{selectedSector.result.action}</span>
                  </div>
                  <div className="flex">
                    <span className="text-brand-blue-bright font-bold w-32 shrink-0">Impact:</span>
                    <span className="text-ink-100">{selectedSector.result.impact}</span>
                  </div>
                </div>
                
                <div className="mt-5 flex flex-wrap gap-3 items-center">
                  <Button
                    size="md"
                    variant="primary"
                    as={Link}
                    to={cta.primary.href}
                    className="px-5 py-2.5 text-xs font-semibold"
                  >
                    Deploy This Stack
                  </Button>
                  <button
                    onClick={resetTerminal}
                    className="px-4 py-2.5 border border-white/10 text-ink-300 hover:text-white text-xs font-semibold font-mono rounded-[10px] hover:bg-white/[.04] transition duration-200"
                  >
                    Reset Terminal
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </TerminalWindow>
  );
}
